type CartAttributeValue = string | null | undefined

type CartAttributionPatch = {
	discount?: CartAttributeValue
	pay_later?: CartAttributeValue
	internal_attrib?: CartAttributeValue
}

type CartAttributes = Record<string, string>

type CartResponse = {
	attributes?: CartAttributes
	cart_level_discount_applications?: Array<{ title?: string }>
}

const DATA_ATTRIBUTE_KEY = 'data'
const INTERNAL_ATTRIB_COOKIE_KEY = 'internal_attrib'

function normalizeValue(value: CartAttributeValue) {
	if (typeof value !== 'string') return ''
	return value.trim()
}

function parseAttributesData(rawData: string | undefined) {
	if (!rawData) return {}
	try {
		const parsed = JSON.parse(rawData)
		if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
			return parsed as Record<string, string>
		}
		return {}
	} catch (_e) {
		return {}
	}
}

function getCookieValue(name: string) {
	const encodedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
	const match = document.cookie.match(new RegExp(`(?:^|; )${encodedName}=([^;]*)`))
	return match ? decodeURIComponent(match[1]) : ''
}

function getInternalAttribFromCookie() {
	return getCookieValue(INTERNAL_ATTRIB_COOKIE_KEY)
}

export async function getCartAttributes() {
	const cart = await fetch('/cart.js', {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	}).then((response) => response.json() as Promise<CartResponse>)

	return cart.attributes || {}
}

export async function updateCartAttributes(patch: CartAttributionPatch) {
	const keys = Object.keys(patch) as (keyof CartAttributionPatch)[]
	if (!keys.length) return

	const currentAttributes = await getCartAttributes()
	const currentData = parseAttributesData(currentAttributes[DATA_ATTRIBUTE_KEY])

	const attributesToUpdate: CartAttributes = {}
	let hasAnyChange = false

	for (const key of keys) {
		const rawValue = patch[key]
		if (typeof rawValue === 'undefined') continue

		const normalized = normalizeValue(rawValue)
		const attributeKey = key as string

		if (normalized) {
			if (currentAttributes[attributeKey] !== normalized) {
				attributesToUpdate[attributeKey] = normalized
				hasAnyChange = true
			}
			currentData[attributeKey] = normalized
		} else {
			if (currentAttributes[attributeKey]) {
				attributesToUpdate[attributeKey] = ''
				hasAnyChange = true
			}
			delete currentData[attributeKey]
		}
	}

	const nextData = Object.keys(currentData).length
		? JSON.stringify(currentData)
		: ''

	if ((currentAttributes[DATA_ATTRIBUTE_KEY] || '') !== nextData) {
		attributesToUpdate[DATA_ATTRIBUTE_KEY] = nextData
		hasAnyChange = true
	}

	if (!hasAnyChange) return

	await fetch('/cart/update.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({
			attributes: attributesToUpdate,
		}),
	})
}

export async function syncDiscountCartAttributeFromCart(cart?: CartResponse) {
	let sourceCart: CartResponse = cart || {}

	if (!cart) {
		sourceCart = await fetch('/cart.js', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}).then((response) => response.json() as Promise<CartResponse>)
	}

	const discountCodes = (sourceCart.cart_level_discount_applications || [])
		.map((discount) => (discount.title || '').trim())
		.filter(Boolean)
		.join(',')

	await updateCartAttributes({ discount: discountCodes || null })
}

export async function syncPayLaterCartAttribute(payLater: CartAttributeValue) {
	await updateCartAttributes({ pay_later: payLater })
}

export async function syncInternalAttribFromCookie() {
	const internalAttrib = getInternalAttribFromCookie()
	if (!internalAttrib) return
	await updateCartAttributes({ internal_attrib: internalAttrib })
}

window.cartAttribution = {
	updateCartAttributes,
	syncDiscountCartAttributeFromCart,
	syncPayLaterCartAttribute,
	syncInternalAttribFromCookie,
}
